import React, { useRef, useMemo, useEffect } from "react";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import { ServiceRow } from "./NewLogicModal";
import ScrollableWrapper from "../common/ScrollableWrapper";
import PageLoader from "../../../../common/ui/PageLoader";
import ServiceEditor from "./ServiceEditor";
import LabPanelEditor from "./LabPanelEditor";
import { useServicesFetcher } from "../common/hooks/useServicesFetcher";
import { validateDurationAttribute } from "../seviceBuilder/NumberAndRangeElement";
import { isQuestionUnanswered } from "../../utils/labPanelQuestions";

interface LogicBuilderStep3Props {
  serviceRows: ServiceRow[];
  services: any[]; // Array from API response
  onServicesDataChange?: (services: any[]) => void;
  labPanels?: any[];
  savedLabPanels?: any[];
  onLabPanelsDataChange?: (labPanels: any[]) => void;
}

const LogicBuilderStep3 = ({
  serviceRows,
  services,
  onServicesDataChange,
  labPanels = [],
  savedLabPanels = [],
  onLabPanelsDataChange,
}: LogicBuilderStep3Props) => {
  const theme: Theme = useTheme();
  const scrollableRef = useRef<HTMLDivElement>(null);

  // Separate service rows from lab panel rows
  const serviceOnlyRows = useMemo(
    () => serviceRows.filter((row) => !row.isLabPanel),
    [serviceRows]
  );

  const labPanelRows = useMemo(
    () => serviceRows.filter((row) => row.isLabPanel),
    [serviceRows]
  );

  const { isLoading, error } = useServicesFetcher({
    serviceRows: serviceOnlyRows,
    services,
    onServicesDataChange,
  });

  // Initialize lab panel configs when lab panel rows change
  useEffect(() => {
    if (!onLabPanelsDataChange) return;

    const currentIds = labPanels.map((lp: any) => lp.userLabPanelID);
    const rowIds = labPanelRows
      .map((row) => row.selectedServiceId)
      .filter((id): id is string => id !== null);

    // Remove configs for rows that no longer exist
    const filtered = labPanels.filter((lp: any) =>
      rowIds.includes(lp.userLabPanelID)
    );

    // Add configs for new rows
    const newConfigs = labPanelRows
      .filter(
        (row) =>
          row.selectedServiceId !== null &&
          !currentIds.includes(row.selectedServiceId)
      )
      .map((row) => {
        // Prefer the panel object carried on the row (covers custom AND
        // provider panels chosen via the modal); fall back to saved-panels.
        const panelData =
          row.labPanelData ||
          savedLabPanels.find(
            (p: any) => p.id.toString() === row.selectedServiceId
          );
        return {
          userLabPanelID: row.selectedServiceId,
          panelData: panelData || null,
          startOffset: 0,
          startOffsetUnit: 4,
        };
      });

    if (newConfigs.length > 0 || filtered.length !== labPanels.length) {
      onLabPanelsDataChange([...filtered, ...newConfigs]);
    }
  }, [labPanelRows, savedLabPanels]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasServices = services.length > 0;
  const hasLabPanels = labPanels.length > 0;

  return (
    <div style={styles.mainContainer}>
      <div style={styles.headerSection}>
        <div style={styles.title(theme)}>Set Values</div>
        <div style={styles.text(theme)}>
          Configure values for service settings. If left blank, services will
          appear with no settings in prescription.
        </div>
      </div>
      <div style={styles.contentWithScroll}>
        <ScrollableWrapper ref={scrollableRef} customPadding={"0px"}>
          <div>
            {isLoading ? (
              <PageLoader />
            ) : error ? (
              <div style={styles.errorContainer(theme)}>
                Error loading services: {error}
              </div>
            ) : (
              <div style={styles.scrollableContent(theme)}>
                {/* Service editors */}
                {services.map((service: any, index: number) => (
                  <div key={service.userServiceID || service.id || index}>
                    {index > 0 && (
                      <div style={styles.serviceSeparator(theme)}></div>
                    )}
                    <div style={styles.serviceCard()}>
                      <ServiceEditor
                        service={service}
                        onServiceChange={(updatedService: any) => {
                          const updatedServices = services.map(
                            (s: any, idx: number) =>
                              idx === index ? updatedService : s
                          );
                          onServicesDataChange?.(updatedServices);
                        }}
                      />
                    </div>
                  </div>
                ))}

                {/* Lab panel editors */}
                {labPanels.map((panelConfig: any, index: number) => {
                  const panelData =
                    panelConfig.panelData ||
                    savedLabPanels.find(
                      (p: any) => p.id.toString() === panelConfig.userLabPanelID
                    );
                  return (
                    <div key={panelConfig.userLabPanelID || index}>
                      {(hasServices || index > 0) && (
                        <div style={styles.serviceSeparator(theme)}></div>
                      )}
                      <div style={styles.serviceCard()}>
                        <LabPanelEditor
                          panelConfig={panelConfig}
                          panelData={panelData}
                          onPanelConfigChange={(updatedConfig: any) => {
                            const updatedPanels = labPanels.map(
                              (lp: any, idx: number) =>
                                idx === index ? updatedConfig : lp
                            );
                            onLabPanelsDataChange?.(updatedPanels);
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </ScrollableWrapper>
      </div>
    </div>
  );
};

// Helper: Check if a value is set (not null, undefined, or 0)
const hasValue = (val: any): boolean =>
  val !== null && val !== undefined && val !== 0;

// Helper: Check if range values are set
const hasRangeValues = (minVal: any, maxVal: any): boolean =>
  hasValue(minVal) || hasValue(maxVal);

// Helper: Get duration label based on service type
const getDurationLabel = (serviceType: number): string => {
  switch (serviceType) {
    case 3:
      return "Total Duration of Supplementation";
    case 4:
      return "Total Duration of Administration";
    default:
      return "Total Treatment Duration";
  }
};

// Helper: Create temporary attribute for duration validation
const createDurationAttribute = (service: any) => ({
  id: `duration-${service.id}`,
  userID: "",
  reportSvcID: "",
  reportSvcAttributeID: "",
  attributeID: `duration-${service.id}`,
  userServiceID: "",
  name: getDurationLabel(service.serviceType),
  type: 1,
  value: service.totalDuration || 0,
  minValue: service.totalDurationMin || 0,
  maxValue: service.totalDurationMax || 0,
  unitValue: service.totalDurationUnit || "",
  unitList: ["days", "weeks", "months", "years"],
  defaultUnit: "months",
  min: "0",
  max: "0",
  text: "",
  textValue: "",
  unit: "",
  timeUnit: 0,
  periodUnit: 0,
  list: [],
});

// Helper: Convert duration value to days for comparison
const convertToDays = (value: number, unit: string): number | null => {
  if (!value || !unit) return null;

  const unitLower = unit.toLowerCase().trim();

  switch (unitLower) {
    case "day":
    case "days":
    case "daily":
      return value;
    case "week":
    case "weeks":
    case "weekly":
      return value * 7;
    case "month":
    case "months":
    case "monthly":
      return value * 30; // Approximate
    case "year":
    case "years":
    case "yearly":
      return value * 365; // Approximate
    case "per week":
    case "per_week":
      return 7 / value; // e.g., 2 per week = every 3.5 days
    case "per month":
    case "per_month":
      return 30 / value;
    case "per day":
    case "per_day":
      return 1 / value;
    default:
      // If unit is not recognized, return null to skip comparison
      return null;
  }
};

// Helper: Calculate days between two dates
const calculateDaysBetweenDates = (
  startDate: string | Date,
  endDate: string | Date
): number | null => {
  try {
    let start: Date;
    let end: Date;

    // Parse startDate
    if (typeof startDate === "string") {
      const dateOnly = startDate.split("T")[0];
      const [year, month, day] = dateOnly.split("-").map(Number);
      start = new Date(year, month - 1, day);
    } else {
      start = new Date(startDate);
    }

    // Parse endDate
    if (typeof endDate === "string") {
      const dateOnly = endDate.split("T")[0];
      const [year, month, day] = dateOnly.split("-").map(Number);
      end = new Date(year, month - 1, day);
    } else {
      end = new Date(endDate);
    }

    // Validate dates
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      return null;
    }

    // Calculate difference in days
    const diffTime = end.getTime() - start.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays > 0 ? diffDays : null;
  } catch (error) {
    return null;
  }
};

// Standalone validation function for Step 3
export const validateStep3 = (
  services: any[],
  type?: string, // 'logicBuilder' or 'prescriptionBuilder'
  originalServices?: any[], // Original services for edit mode comparison
  labPanels?: any[]
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  const hasServices = services && services.length > 0;
  const hasLabPanels = labPanels && labPanels.length > 0;

  if (!hasServices && !hasLabPanels) {
    errors.push("Please add at least one service or lab panel to configure");
    return { isValid: false, errors };
  }

  // Validate lab panels
  if (hasLabPanels) {
    labPanels!.forEach((lp: any, index: number) => {
      const panelName = lp.panelData?.name || `Lab Panel ${index + 1}`;

      if (type === "prescriptionBuilder") {
        if (!lp.startDate) {
          errors.push(`${panelName}: Please select a start date`);
        }
        // Every order-entry question the lab asks is required: the order
        // cannot be placed without the answers.
        const unanswered = Array.isArray(lp.questions)
          ? lp.questions.filter(isQuestionUnanswered).length
          : 0;
        if (unanswered > 0) {
          errors.push(
            `${panelName}: Please answer ${
              unanswered === 1
                ? "the remaining question"
                : `all ${unanswered} remaining questions`
            } the lab requires`
          );
        }
      } else {
        if (lp.startOffsetUnit === null || lp.startOffsetUnit === undefined) {
          errors.push(`${panelName}: Please select a start offset unit`);
        }
      }
    });
  }

  if (!hasServices) {
    return { isValid: errors.length === 0, errors };
  }

  services.forEach((service: any, serviceIndex: number) => {
    const serviceName = service.name || `Service ${serviceIndex + 1}`;
    const originalService = originalServices?.[serviceIndex];

    // Validate startDate for prescription builder
    if (type === "prescriptionBuilder") {
      if (!service.startDate) {
        errors.push(`${serviceName}: Please select a start date`);
      } else {
        // Handle both Date objects and string dates
        let startDate: Date;
        if (typeof service.startDate === "string") {
          // Handle different string formats:
          // "YYYY-MM-DD" or "YYYY-MM-DDTHH:mm:ss"
          const dateOnly = service.startDate.split("T")[0]; // Get date part before time
          const [year, month, day] = dateOnly.split("-").map(Number);
          startDate = new Date(year, month - 1, day);
        } else {
          startDate = new Date(service.startDate);
        }

        if (isNaN(startDate.getTime())) {
          errors.push(`${serviceName}: Please select a valid start date`);
        } else {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          startDate.setHours(0, 0, 0, 0);

          // Check if date is in the past
          if (startDate < today) {
            // If editing and the date matches the original, allow it
            let isEditingWithOriginalDate = false;
            if (originalService?.startDate) {
              // Normalize both dates to YYYY-MM-DD format for comparison
              let originalDateStr: string;
              if (typeof originalService.startDate === "string") {
                originalDateStr = originalService.startDate.split("T")[0];
              } else {
                const d = new Date(originalService.startDate);
                const year = d.getFullYear();
                const month = String(d.getMonth() + 1).padStart(2, "0");
                const day = String(d.getDate()).padStart(2, "0");
                originalDateStr = `${year}-${month}-${day}`;
              }

              let currentDateStr: string;
              if (typeof service.startDate === "string") {
                currentDateStr = service.startDate.split("T")[0];
              } else {
                const d = new Date(service.startDate);
                const year = d.getFullYear();
                const month = String(d.getMonth() + 1).padStart(2, "0");
                const day = String(d.getDate()).padStart(2, "0");
                currentDateStr = `${year}-${month}-${day}`;
              }

              isEditingWithOriginalDate = originalDateStr === currentDateStr;
            }

            if (!isEditingWithOriginalDate) {
              errors.push(`${serviceName}: Start date cannot be in the past`);
            }
          }
        }
      }
    }

    // Validate endDate for prescription builder (only when field is shown)
    if (
      type === "prescriptionBuilder" &&
      service.serviceType !== 7 &&
      service.serviceType !== 6
    ) {
      if (!service.endDate) {
        errors.push(`${serviceName}: Please select an end date`);
      } else {
        // Handle both Date objects and string dates
        let endDate: Date;
        if (typeof service.endDate === "string") {
          const dateOnly = service.endDate.split("T")[0];
          const [year, month, day] = dateOnly.split("-").map(Number);
          endDate = new Date(year, month - 1, day);
        } else {
          endDate = new Date(service.endDate);
        }

        if (isNaN(endDate.getTime())) {
          errors.push(`${serviceName}: Please select a valid end date`);
        } else {
          // Validate that endDate is after startDate
          if (service.startDate) {
            let startDate: Date;
            if (typeof service.startDate === "string") {
              const dateOnly = service.startDate.split("T")[0];
              const [year, month, day] = dateOnly.split("-").map(Number);
              startDate = new Date(year, month - 1, day);
            } else {
              startDate = new Date(service.startDate);
            }

            startDate.setHours(0, 0, 0, 0);
            endDate.setHours(0, 0, 0, 0);

            if (endDate <= startDate) {
              errors.push(`${serviceName}: End date must be after start date`);
            }
          }
        }
      }
    }

    // Validate Total Duration field (only for logicBuilder type)
    if (
      type === "logicBuilder" &&
      service.serviceType !== 7 &&
      service.serviceType !== 6
    ) {
      const hasDurationValue = hasValue(service.totalDuration);
      const hasDurationRange = hasRangeValues(
        service.totalDurationMin,
        service.totalDurationMax
      );
      const durationLabel = getDurationLabel(service.serviceType);

      if (!hasDurationValue && !hasDurationRange) {
        errors.push(
          `${serviceName}: Please complete the "${durationLabel}" field`
        );
      } else if (!service.totalDurationUnit?.trim()) {
        errors.push(
          `${serviceName}: Please select a unit for "${durationLabel}"`
        );
      } else {
        const validationError = validateDurationAttribute(
          createDurationAttribute(service)
        );
        if (validationError) {
          errors.push(`${serviceName} - ${durationLabel}: ${validationError}`);
        }
      }
    }

    // Validate service attributes (only validate filled fields, don't require all)
    if (service.attributes?.length > 0) {
      service.attributes.forEach((attribute: any) => {
        const hasSingleValue = hasValue(attribute.value);
        const hasRangeValue = hasRangeValues(
          attribute.minValue,
          attribute.maxValue
        );
        const hasAnyValue = hasSingleValue || hasRangeValue;

        switch (attribute.type) {
          case 2: // Text input
          case 6: // List selection
            // No validation needed for text - any text is valid
            break;

          case 3: // Numeric input (can be single or range)
            // Only validate if values are provided
            if (hasAnyValue) {
              const validationError = validateDurationAttribute(attribute);
              if (validationError) {
                errors.push(
                  `${serviceName} - ${attribute.name}: ${validationError}`
                );
              }
            }
            break;

          case 1: // Range (label/min/max/unit)
          case 4: // Time (label/time/time unit)
          case 5: // Time range (label/time min/time max/time unit)
          case 7: // Period range (label/time min/time max/time unit)
            // Only validate if values are provided
            if (hasAnyValue) {
              // These types require units when values are present
              if (!attribute.unitValue?.trim()) {
                errors.push(
                  `${serviceName} - ${attribute.name}: Please select a unit`
                );
              } else {
                const validationError = validateDurationAttribute(attribute);
                if (validationError) {
                  errors.push(
                    `${serviceName} - ${attribute.name}: ${validationError}`
                  );
                }
              }
            }
            break;
        }
      });
    }

    // Helper function to validate frequency attribute against total treatment duration
    const validateFrequencyAttribute = (
      frequencyAttrName: string,
      service: any,
      serviceName: string,
      type?: string
    ) => {
      const frequencyAttr = service.attributes?.find(
        (attr: any) => attr.name === frequencyAttrName
      );

      if (frequencyAttr) {
        const hasFreqValue = hasValue(frequencyAttr.value);
        const hasFreqRange = hasRangeValues(
          frequencyAttr.minValue,
          frequencyAttr.maxValue
        );
        const freqUnit = frequencyAttr.unitValue?.trim().toLowerCase() || "";

        // Special validation: one-off frequency requires 1 day duration
        if (freqUnit === "one-off") {
          if (type === "logicBuilder") {
            const hasTotalDurationValue = hasValue(service.totalDuration);
            const hasTotalDurationRange = hasRangeValues(
              service.totalDurationMin,
              service.totalDurationMax
            );

            if (hasTotalDurationValue || hasTotalDurationRange) {
              const durationValue = hasTotalDurationRange
                ? service.totalDurationMax || service.totalDurationMin || 0
                : service.totalDuration;
              const durationUnit =
                service.totalDurationUnit?.trim().toLowerCase() || "months";

              const isInvalid =
                durationValue !== 1 ||
                (durationUnit !== "day" && durationUnit !== "days");

              if (isInvalid) {
                errors.push(
                  `${serviceName}: Total Treatment Duration must be exactly 1 day when ${frequencyAttrName} is one-off`
                );
              }
            }
          } else if (type === "prescriptionBuilder") {
            if (service.startDate && service.endDate) {
              const days = calculateDaysBetweenDates(
                service.startDate,
                service.endDate
              );

              if (days !== null && days !== 1) {
                errors.push(
                  `${serviceName}: Prescription Period must be exactly 1 day when ${frequencyAttrName} is one-off`
                );
              }
            }
          }
        }

        if (hasFreqValue || hasFreqRange) {
          let totalTreatmentDurationInDays: number | null = null;

          // Calculate Total Treatment Duration based on type
          if (type === "logicBuilder") {
            // For LogicBuilder, use totalDuration fields
            const hasTotalDurationValue = hasValue(service.totalDuration);
            const hasTotalDurationRange = hasRangeValues(
              service.totalDurationMin,
              service.totalDurationMax
            );

            if (hasTotalDurationValue || hasTotalDurationRange) {
              // For ranges, use MAX value (best case - longest treatment period)
              // For single values, use the value itself
              const durationValue = hasTotalDurationRange
                ? service.totalDurationMax || service.totalDurationMin || 0
                : service.totalDuration;
              const durationUnit =
                service.totalDurationUnit?.trim() || "months";

              totalTreatmentDurationInDays = convertToDays(
                durationValue,
                durationUnit
              );
            }
          } else if (type === "prescriptionBuilder") {
            // For PrescriptionBuilder, calculate from startDate and endDate
            if (service.startDate && service.endDate) {
              totalTreatmentDurationInDays = calculateDaysBetweenDates(
                service.startDate,
                service.endDate
              );
            }
          }

          // Compare if both values are available
          if (totalTreatmentDurationInDays !== null) {
            // For frequency attributes, we need to check if the duration is at least
            // one period of the frequency unit (e.g., "400 yearly" needs at least 1 year)
            // The value represents how many sessions in that period
            const minimumRequiredDays = convertToDays(1, freqUnit);

            if (
              minimumRequiredDays !== null &&
              minimumRequiredDays > totalTreatmentDurationInDays
            ) {
              const durationLabel =
                type === "prescriptionBuilder"
                  ? "Prescription Period"
                  : "Total Treatment Duration";
              errors.push(
                `${serviceName}: The ${frequencyAttrName} attribute should always be equal or lower than ${durationLabel}`
              );
            }
          }
        }
      }
    };

    // Cross-field validation: Frequency vs Total Treatment Duration
    if (service.serviceType !== 7 && service.serviceType !== 6) {
      if (service.serviceType === 3) {
        // For service type 3 (supplements), validate Administration Frequency
        validateFrequencyAttribute(
          "Administration Frequency",
          service,
          serviceName,
          type
        );
      } else {
        // For other service types, validate Sessions Frequency
        validateFrequencyAttribute(
          "Sessions Frequency",
          service,
          serviceName,
          type
        );
      }
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
  };
};

export default LogicBuilderStep3;

const styles = {
  mainContainer: {
    flex: 1,
    display: "flex",
    flexDirection: "column" as "column",
    minHeight: 0,
    maxHeight: "100%",
    overflow: "hidden",
  },
  headerSection: {
    marginBottom: "16px",
  },
  contentWithScroll: {
    flex: 1,
    display: "flex",
    flexDirection: "column" as "column",
    minHeight: 0,
    maxHeight: "100%",
    overflow: "hidden",
  },
  scrollableContent: (theme: Theme) => ({
    display: "flex",
    flexDirection: "column" as "column",
    paddingBottom: "20px",
    borderRadius: "8px",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    backgroundColor: theme.colors_ds.NEUTRAL_GENERAL_WHITE,
    overflow: "hidden",
  }),
  title: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "20px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  text: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "17px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    marginTop: "4px",
  }),
  serviceCard: () => ({
    padding: "0",
  }),
  errorContainer: (theme: Theme) => ({
    padding: "16px",
    borderRadius: "8px",
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    color: theme.colors_ds.DANGER_GRAPH_DEFICIT,
  }),
  serviceSeparator: (theme: Theme) => ({
    margin: "8px 16px",
    borderBottom: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
  }),
};
